import React, { useMemo } from 'react';
import { useSignals } from '@preact/signals-react/runtime';
import { Row } from 'antd';
import Icon from 'assets/Icons';
import Box from 'components/Box';
import Text from 'components/Text';
import appointmentActions from 'features/appointment/services/actions';
import {
  APPOINTMENT_SOURCE_TYPE,
  APPOINTMENT_STATUS,
  CALENDAR_ITEM_TYPES,
  GROUP_CONFIG,
  PROLONGED_TIME_STATUS,
  appointmentNoneEdit,
  appointmentShowRequestStaff,
  getAptStatusTheme,
} from 'features/appointment/services/constants';
import { IAppointmentItemData } from 'features/appointment/services/types/appointment';
import { ICalendarHeader, ICalendarItemData } from 'features/appointment/services/types/calendar';
import { FORMAT_TIME } from 'features/appointment/utils/format';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import storage from 'utils/sessionStorage';
import { momentTimezone } from 'utils/time';
import DraggableItem from './DnD/DraggableItem';
import ic_by_app from './icons/ic_by-app.svg';
import ic_calling from './icons/ic_calling.svg';
import ic_online from './icons/ic_online.svg';
import ic_request_staff from './icons/ic_request-staff.svg';
import ic_paid from './icons/ic_paid.svg';
import ic_cancel from './icons/ic_cancel.svg';
import ic_group from './icons/ic_group.svg';
import ic_request from './icons/ic_request.png';
import CustomerPopoverCheckPinCode from './CustomerPopoverCheckPinCode';
import appointmentSelectors from 'features/appointment/services/selectors';
import { appointmentDetailRef } from 'features/appointment/pages/AppointmentsPage/components/Calendar/TableView/Body/Modal/AppointmentDetail';
import { aptDetailV3Ref } from './Modal/AppointmentDetailV3';

type IAppointmentProps = {
  data: ICalendarItemData;
  isMulti?: boolean;
  headerData?: ICalendarHeader;
  isMonthView?: boolean;
  dateOfMonth?: string;
  v2?: boolean;
};
const Appointment: React.FC<IAppointmentProps> = ({
  v2,
  data,
  isMulti = false,
  isMonthView,
  dateOfMonth,
  headerData,
}) => {
  useSignals();
  const enableDrag = appointmentSelectors.enableDrag();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const time = useMemo(() => {
    const startTime = momentTimezone(data.startTime);
    const endTime = momentTimezone(data.endTime);
    return `${startTime.format(FORMAT_TIME)} - ${endTime.format(FORMAT_TIME)}`;
  }, [data]);
  const extraData = useMemo(() => {
    return data.data as IAppointmentItemData;
  }, [data]);

  const addMoreTimeAppointment: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    dispatch(
      appointmentActions.addMoreTimeAppointment({
        status: PROLONGED_TIME_STATUS.ADD,
        appointmentId: data.id,
      })
    );
  };

  const minusTimeAppointment: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    dispatch(
      appointmentActions.addMoreTimeAppointment({
        status: PROLONGED_TIME_STATUS.MINUS,
        appointmentId: data.id,
      })
    );
  };

  const gotoDetail = () => {
    if (extraData.version === 'v3')
      return aptDetailV3Ref.current?.open(data.id);
    appointmentDetailRef.current?.open({
      data: data,
      headerData: headerData || null,
    });
  };

  const getIcons = () => {
    switch (extraData.sourceType) {
      case APPOINTMENT_SOURCE_TYPE.POS:
        return <IconCalling />;
      case APPOINTMENT_SOURCE_TYPE.APP:
        return <IconByApp />;
      default:
        return <IconOnline />;
    }
  };
  if (data.type !== CALENDAR_ITEM_TYPES.APPOINTMENT) return null;

  const dataCustomer = data.data as IAppointmentItemData;
  const Container = () => (
    <AppointmentStyled
      id={data.id}
      className={'AppointmentStyled'}
      status={extraData.status}
      isMulti={isMulti}
      onClick={
        ((e) => [e.stopPropagation(), gotoDetail()]) as React.MouseEventHandler<HTMLDivElement>
      }
    >
      <div
        className={`appointment-box ${extraData.sourceType === APPOINTMENT_SOURCE_TYPE.ONLINE ? 'apt-online' : ''
          }`}
      >
        {dateOfMonth && (
          <Text variant='BODY_1' color='text_2' mb={1} p={0.25} textAlign='left'>
            {dateOfMonth}
          </Text>
        )}
        <div style={{ textAlign: 'left' }}>
          <Box display='flex' alignItems='center'>
            <CustomerPopoverCheckPinCode
              customerName={extraData.customerName || ''}
              customerId={dataCustomer.customerId || ''}
            />

            <AppointmentType>
              {/* {extraData.requestStaff && <IconRequestStaff />} */}
              {getIcons()}
            </AppointmentType>
          </Box>
          <Text variant='CAPTION_3' color='text_3' style={{ fontSize: '1rem' }}>
            {time}
          </Text>
          {(extraData.numberCustomers || 0) > 1 && <Box display='flex' pt='1'>
            <GroupBoxStyled style={{ background: GROUP_CONFIG.value.fill }}>
              <img src={ic_group} style={{ width: 20, height: 20 }} />
              <span>Party of {extraData.numberCustomers || 0}</span>
            </GroupBoxStyled>
          </Box>}
        </div>
        {extraData.note && (
          <Row align={'middle'}>
            <Text variant='CAPTION_2' color='text_3' style={{ fontSize: '1.25rem' }} mr={0.25}>
              {'Note: '}
            </Text>
            <Text
              variant='CAPTION_3'
              color='text_3'
              style={{ textAlign: 'left', fontSize: '1.25rem' }}
            >
              {extraData?.note}
            </Text>
          </Row>
        )}
        <div className='flex-grow' />
        {extraData.services.map((o) => (
          <Row key={o.serviceId} justify={'start'}>
            <Text variant='H7' color='text_3' style={{ textAlign: 'left' }}>
              {o.serviceName} ({o.duration}m)
            </Text>
          </Row>
        ))}
      </div>
      {extraData.status === APPOINTMENT_STATUS.PAID && <IconPaid />}
      {extraData.status === APPOINTMENT_STATUS.CANCEL && <IconCancel />}
      {extraData.requestStaff && appointmentShowRequestStaff.includes(extraData.status) && (
        <IconRequest />
      )}
      {!headerData?.modal && (
        <>
          {!isMonthView && !appointmentNoneEdit.includes(extraData.status) && (
            <Row className='bottom' align={'middle'} justify={'center'} style={{ gap: '1rem' }}>
              <Row align={'middle'} justify={'center'}>
                <button onClick={minusTimeAppointment}>
                  <Icon type='minusAppointment' />
                </button>
              </Row>
              <Row align={'middle'} justify={'center'}>
                <button onClick={addMoreTimeAppointment}>
                  <Icon type='addAppointment' />
                </button>
              </Row>
            </Row>
          )}
        </>
      )}
    </AppointmentStyled>
  );
  if (!enableDrag) return <Container />;

  return (
    <DraggableItem key={data?.id} id={data?.id?.toString()} data={{ data }}>
      <Container />
    </DraggableItem>
  );
};

export default Appointment;

type AppointmentStyledProps = {
  isMulti: boolean;
  status?: APPOINTMENT_STATUS;
};

const AppointmentStyled = styled.div<AppointmentStyledProps>`
  height: 100%;
  width: 100%;
  display: flex;

  .appointment-box {
    border-radius: 5px;
    ${({ status }) => {
    const theme = getAptStatusTheme(status);
    return `
      border: 1.5px solid ${theme.stroke};
      background: ${theme.fill};
      `;
  }}
    box-shadow: -1px 1px 4px 0px rgba(0, 0, 0, 0.10) inset;
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));

    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 4px 8px;
    padding-bottom: 1.25rem;
    flex: 1;
    overflow: hidden;
    ${({ isMulti }) =>
    isMulti
      ? `
      border-radius: 5px;
      border: 1px solid var(--primary-primary-hover, #F5767F);
      background: #E7FBFF;
    `
      : ''}
    .flex-grow {
      flex: 1;
    }
  }
  .apt-online {
  }
  .bottom {
    position: absolute;
    bottom: 0;
    width: 100%;
    transform: translateY(12px);
    button {
      border-radius: 100%;
    }
  }
`;

const AppointmentType = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
const IconStyle = styled.img`
  &.icon-appointment {
    width: 40px;
    height: 40px;
  }
`;
const IconPaidStyled = styled.img`
  position: absolute;
  width: 65.383px !important;
  height: auto;
  right: 10px;
  bottom: 8px;
  background: transparent;
`;
const IconCancelStyled = styled(IconPaidStyled)``;
const IconRequestStyled = styled.img`
  position: absolute;
  width: 100px !important;
  right: 3px;
  bottom: 10px;
  background: transparent;
`;

const GroupBoxStyled = styled.div`
display: flex;
padding: 2px 4px;
justify-content: center;
align-items: center;
gap: 2px;
border-radius: 4px;
background: #4384FF;
span {
  color: #FFF;
  text-align: center;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}
`;

const IconCalling = () => <IconStyle className='icon-appointment' src={ic_calling} alt='calling' />;

const IconOnline = () => <IconStyle className='icon-appointment' src={ic_online} alt='online' />;

const IconByApp = () => <IconStyle className='icon-appointment' src={ic_by_app} alt='by-app' />;

const IconRequestStaff = () => (
  <IconStyle className='icon-appointment' src={ic_request_staff} alt='request-staff' />
);

const IconPaid = () => <IconPaidStyled className='icon-paid' src={ic_paid} alt='paid' />;

const IconCancel = () => <IconCancelStyled className='icon-cancel' src={ic_cancel} alt='cancel' />;

const IconRequest = () => (
  <IconRequestStyled className='icon-request' src={ic_request} alt='request' />
);
